/**
 * Phase 4 — "Remind me later" says what it did: the card becomes a quiet
 * "Hidden until Wed 7 Oct · Undo" row. The date is the cool-off Express saved
 * for this person; until that answer arrives (or if it lacks one) we show
 * the configured snooze length from now.
 */

/** Mirrors Express `config/coolOffRules.js` `snoozed.coolOffDays`. */
export const OLLIE_SNOOZE_FALLBACK_DAYS = 7

const MS_PER_DAY = 24 * 60 * 60 * 1000

export const OLLIE_SNOOZE_CONFIRM_COPY = Object.freeze({
  hiddenUntil: 'Hidden until {date}',
  hiddenUntilTomorrow: 'Hidden until tomorrow',
  undo: 'Undo',
  undoFailed: "Couldn't undo that. Try again.",
})

const HIDDEN_UNTIL_FORMAT = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/London',
})

const toValidDate = value => {
  if (value == null || value === '') return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * The finding's snooze length (Express `snoozeDays`, e.g. 1 for Food Safety)
 * from `now`, for before / without the server's date.
 */
export function fallbackOllieHiddenUntil(
  now = Date.now(),
  snoozeDays = OLLIE_SNOOZE_FALLBACK_DAYS,
) {
  const days =
    Number.isFinite(snoozeDays) && snoozeDays > 0
      ? snoozeDays
      : OLLIE_SNOOZE_FALLBACK_DAYS
  return new Date(new Date(now).getTime() + days * MS_PER_DAY)
}

/**
 * The hidden-until date from the snooze response (`intentState.coolOffUntil`),
 * else the configured snooze length from `now`.
 */
export function resolveOllieHiddenUntil(
  response,
  now = Date.now(),
  snoozeDays = OLLIE_SNOOZE_FALLBACK_DAYS,
) {
  return (
    toValidDate(response?.intentState?.coolOffUntil) ||
    fallbackOllieHiddenUntil(now, snoozeDays)
  )
}

/** UK short date, e.g. "Wed 7 Oct". */
export function formatOllieHiddenUntil(date) {
  const valid = toValidDate(date)
  return valid ? HIDDEN_UNTIL_FORMAT.format(valid) : ''
}

const UK_DAY_KEY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London',
})

/** "2026-10-02" in London. */
const ukDayKey = date => UK_DAY_KEY_FORMAT.format(date)

/** "Hidden until tomorrow" for the next UK day, else "Hidden until Wed 7 Oct". */
export function ollieHiddenUntilLabel(date, now = Date.now()) {
  const valid = toValidDate(date)
  if (valid) {
    const tomorrow = new Date(new Date(now).getTime() + MS_PER_DAY)
    if (ukDayKey(valid) === ukDayKey(tomorrow)) {
      return OLLIE_SNOOZE_CONFIRM_COPY.hiddenUntilTomorrow
    }
  }
  const formatted = formatOllieHiddenUntil(date)
  return formatted
    ? OLLIE_SNOOZE_CONFIRM_COPY.hiddenUntil.replace('{date}', formatted)
    : ''
}
